// The toy audience (§12.6): plush critters (bears, bunnies and cats from one InstancedMesh: ear
// vertices reshaped per instance) in two bleacher rows on balloon-borne shelves in the fog band,
// 120 / 60 / 24 / 0 by tier (plus an instanced outline on High).
// A vertex-shader bounce driven by `excitement` (hop 0.05 + 0.4 × excitement, on the beat, per-
// instance phase) and a jump wave that sweeps around the ring on every KO.
import {
  BufferAttribute, Color, ConeGeometry, CylinderGeometry, IcosahedronGeometry, InstancedBufferAttribute, InstancedMesh, Matrix4, Object3D, SphereGeometry, Vector3,
  type BufferGeometry, type Texture, type ColorRepresentation, type IUniform,
} from 'three';
import type { QualityTier } from '../../Renderer';
import { Batch, hullGeometry, merge, prep, seeded } from '../kit/geometry';
import { outlineMaterial, surfaceMaterial, type EnvUniforms } from '../kit/materials';

export const AUDIENCE_COUNT: Readonly<Record<QualityTier, number>> = { high: 120, medium: 60, low: 24, potato: 0 };

export interface AudienceLayout {
  /** Ellipse the shelves sit on (sim units, around the arena center). */
  cx: number; cy: number; rx: number; ry: number;
  /** three.js height of the shelf tops. */
  y: number;
  /** Shelf colors (plank, trim). */
  plank: ColorRepresentation; trim: ColorRepresentation;
  /** Critter fur colors. */
  fur: readonly ColorRepresentation[];
}

export interface Audience {
  readonly root: Object3D;
  /** Static shelf geometry, merged by the caller into its vertex-colored bucket. */
  addShelves(batch: Batch, bucket: string, outlineBucket: string | null): void;
  /** Starts a jump wave from the sim point (x, y). */
  wave(x: number, y: number, now: number): void;
  dispose(): void;
}

const SHELF_LEN = 3.6;
const SHELF_DEPTH = 1.0;
const SHELF_THICK = 0.22;
/** The back row's riser: its top above the shelf's center height, and how far back it sits. */
const RISER_TOP = 0.36;
const RISER_BACK = 0.26;
/** The front row sits this far toward the arena from the shelf's center line. */
const FRONT_ROW = 0.2;
/** Angles (radians, sim) that get no shelves: the camera side, where they would never be seen. */
const FRONT_GAP = 0.9;

const PARS = /* glsl */ `
uniform vec3 uWave;
varying float vHopShade;
`;
const VERTEX_PARS = 'attribute vec3 aHop;\nattribute float aKind;\nattribute float aEar;\n';

// aHop: x = beat phase, y = ring angle, z = hop scale. aKind: 0 bear (round ears), 1 bunny (long
// ears), 2 cat (pointed ears). aEar marks ear vertices.
const VERTEX = /* glsl */ `
  if (aEar > 0.5) {
    vec3 base = vec3(sign(transformed.x) * 0.13, 0.66, 0.0);
    vec3 e = transformed - base;
    if (aKind > 1.5) {
      // Cat: taller, pinched toward a point at the tip.
      float t = clamp(e.y / 0.2, 0.0, 1.0);
      e.xz *= 1.0 - 0.6 * t;
      e.y *= 1.35;
    } else if (aKind > 0.5) {
      // Bunny: long, narrow, leaning out a little.
      e.y *= 2.6;
      e.x *= 0.85;
      e.x += sign(base.x) * e.y * 0.18;
    } else {
      // Bear: small and round.
      e *= vec3(1.15, 0.8, 1.0);
      e.y -= 0.02;
    }
    transformed = base + e;
  }
  {
    float excite = clamp(uExcite, 0.0, 1.0);
    float beatPhase = fract(uBeat + aHop.x);
    float hop = (0.05 + 0.4 * excite) * aHop.z * sin(3.14159265 * beatPhase);
    float waveAge = uTime - uWave.z;
    float wave = 0.0;
    if (waveAge > 0.0 && waveAge < 2.4) {
      float front = uWave.x + uWave.y * waveAge * 3.2;
      float d = abs(mod(aHop.y - front + 3.14159265, 6.2831853) - 3.14159265);
      wave = 0.75 * exp(-d * d * 9.0) * (1.0 - waveAge / 2.4);
    }
    float lift = hop + wave;
    float squash = 1.0 + 0.18 * (lift - 0.1);
    transformed.y = transformed.y * squash + lift;
    transformed.xz *= 1.0 / sqrt(max(squash, 0.6));
    vHopShade = lift;
  }
`;

/**
 * One critter: round body, head, ears, eyes and a cream muzzle, merged with vertex colors (about
 * 240 triangles on High, so 120 critters stay inside the map budget).
 */
function critterGeometry(tier: QualityTier): BufferGeometry {
  const w = tier === 'high' ? 10 : 8;
  const h = tier === 'high' ? 7 : 5;
  const parts: BufferGeometry[] = [];
  /** Per part: 1 = ear (the vertex shader shapes it by the critter's kind), 0 = anything else. */
  const tags: number[] = [];
  const white = '#FFFFFF';
  parts.push(prep(new SphereGeometry(0.24, w, h), new Matrix4().makeScale(1, 0.92, 0.95).setPosition(0, 0.22, 0), { color: white, ao: [0, 0.3, 0.6] }));
  tags.push(0);
  parts.push(prep(new SphereGeometry(0.2, w, h), new Matrix4().makeTranslation(0, 0.56, 0.02), { color: white }));
  tags.push(0);
  for (const s of [-1, 1]) {
    parts.push(prep(new SphereGeometry(0.085, 6, 4), new Matrix4().makeScale(0.8, 1.25, 0.6).setPosition(s * 0.13, 0.74, 0), { color: white }));
    tags.push(1);
    parts.push(prep(new IcosahedronGeometry(0.036, 0), new Matrix4().makeTranslation(s * 0.075, 0.6, 0.19), { color: '#1A1020' }));
    tags.push(0);
    // Rosy cheeks.
    parts.push(prep(new SphereGeometry(0.035, 5, 3), new Matrix4().makeScale(1.2, 0.7, 0.5).setPosition(s * 0.12, 0.52, 0.17), { color: '#FF8FA8' }));
    tags.push(0);
  }
  parts.push(prep(new SphereGeometry(0.09, 6, 4), new Matrix4().makeScale(1.1, 0.75, 0.6).setPosition(0, 0.5, 0.17), { color: '#FFF6E9' }));
  tags.push(0);
  const ear = new Float32Array(parts.reduce((n, g) => n + g.getAttribute('position').count, 0));
  let at = 0;
  parts.forEach((g, i) => {
    const n = g.getAttribute('position').count;
    ear.fill(tags[i], at, at + n);
    at += n;
  });
  const out = merge(parts);
  out.setAttribute('aEar', new BufferAttribute(ear, 1));
  return out;
}

/** The outline hull of a critter: body and head only, low-poly. */
function critterHull(): BufferGeometry {
  const parts: BufferGeometry[] = [];
  parts.push(prep(new SphereGeometry(0.24, 8, 5), new Matrix4().makeScale(1, 0.92, 0.95).setPosition(0, 0.22, 0)));
  parts.push(prep(new SphereGeometry(0.2, 8, 5), new Matrix4().makeTranslation(0, 0.56, 0.02)));
  return merge(parts);
}

export function createAudience(layout: AudienceLayout, tier: QualityTier, seed: number, env: EnvUniforms, ramp: Texture,
  outlineScale: IUniform<number>): Audience {
  const root = new Object3D();
  root.name = 'audience';
  const count = AUDIENCE_COUNT[tier];
  const rnd = seeded(seed * 7 + 3);
  const owned: { dispose(): void }[] = [];

  // Shelves around the ellipse, skipping the camera-facing arc.
  const shelves: { x: number; y: number; angle: number; len: number; height: number }[] = [];
  const circumference = Math.PI * (3 * (layout.rx + layout.ry) - Math.sqrt((3 * layout.rx + layout.ry) * (layout.rx + 3 * layout.ry)));
  const nSlots = Math.max(6, Math.floor(circumference / (SHELF_LEN + 2.2)));
  const slots: number[] = [];
  for (let i = 0; i < nSlots; i++) {
    const a = (i / nSlots) * Math.PI * 2 + 0.2;
    // Sim +y faces the camera: skip shelves centered within FRONT_GAP of +π/2.
    const fromFront = Math.abs(Math.atan2(Math.sin(a - Math.PI / 2), Math.cos(a - Math.PI / 2)));
    if (fromFront >= FRONT_GAP) slots.push(a);
  }
  // About 8 critters per shelf: a small crowd fills a few shelves spread around the ring rather
  // than dotting every shelf.
  const wanted = Math.min(slots.length, Math.max(2, Math.ceil(count / 8)));
  slots.sort((p, q) => Math.atan2(Math.sin(p + Math.PI / 2), Math.cos(p + Math.PI / 2)) - Math.atan2(Math.sin(q + Math.PI / 2), Math.cos(q + Math.PI / 2)));
  for (let k = 0; k < wanted; k++) {
    const a = slots[Math.min(slots.length - 1, Math.round(((k + 0.5) * slots.length) / wanted - 0.5))];
    const x = layout.cx + Math.cos(a) * layout.rx;
    const y = layout.cy + Math.sin(a) * layout.ry;
    // Tangent of the ellipse.
    const tx = -Math.sin(a) * layout.rx;
    const ty = Math.cos(a) * layout.ry;
    shelves.push({ x, y, angle: Math.atan2(ty, tx), len: SHELF_LEN * (0.85 + rnd() * 0.3), height: layout.y + (rnd() - 0.5) * 0.9 });
  }

  // Critters spread over the shelves.
  const geo = critterGeometry(tier);
  owned.push(geo);
  let mesh: InstancedMesh | null = null;
  let hull: InstancedMesh | null = null;
  const waveU: IUniform<Vector3> = { value: new Vector3(0, 1, -100) };
  if (count > 0 && shelves.length > 0) {
    const mat = surfaceMaterial(env, ramp, {
      key: 'audience', rim: 0.45, gloss: 0.15, pars: PARS, vertexPars: VERTEX_PARS, vertex: VERTEX,
      uniforms: { uWave: waveU },
      vertexColors: true,
    });
    owned.push(mat);
    mesh = new InstancedMesh(geo, mat, count);
    const hop = new Float32Array(count * 3);
    const kinds = new Float32Array(count);
    const m = new Matrix4();
    const sc = new Matrix4();
    const c = new Color();
    const perShelf = Math.ceil(count / shelves.length);
    let i = 0;
    for (let s = 0; s < shelves.length && i < count; s++) {
      const sh = shelves[s];
      // Two rows like bleachers: the front row on the plank, the back row up on the riser.
      const inX = layout.cx - sh.x;
      const inY = layout.cy - sh.y;
      const inL = Math.hypot(inX, inY) || 1;
      const front = Math.ceil(perShelf / 2);
      for (let k = 0; k < perShelf && i < count; k++, i++) {
        const back = k >= front;
        const n = back ? perShelf - front : front;
        const j = back ? k - front : k;
        const t = (j + (back ? 0.5 : 0.5)) / n - 0.5;
        const along = t * (sh.len - (back ? 1.0 : 0.6)) + (back ? 0.12 : 0);
        const depth = (back ? -RISER_BACK : FRONT_ROW) + (rnd() - 0.5) * 0.08;
        const px = sh.x + Math.cos(sh.angle) * along + (inX / inL) * depth;
        const py = sh.y + Math.sin(sh.angle) * along + (inY / inL) * depth;
        // Face the arena center.
        const face = Math.atan2(layout.cy - py, layout.cx - px);
        const scale = 0.85 + rnd() * 0.45;
        m.makeRotationY(Math.PI / 2 - face).multiply(sc.makeScale(scale, scale, scale));
        m.setPosition(px, sh.height + (back ? RISER_TOP : SHELF_THICK / 2), py);
        kinds[i] = Math.floor(rnd() * 3);
        mesh.setMatrixAt(i, m);
        c.set(layout.fur[Math.floor(rnd() * layout.fur.length)]);
        mesh.setColorAt(i, c);
        const ringAngle = Math.atan2(py - layout.cy, px - layout.cx);
        hop[i * 3] = rnd();
        hop[i * 3 + 1] = ringAngle;
        hop[i * 3 + 2] = 0.7 + rnd() * 0.6;
      }
    }
    mesh.count = i;
    geo.setAttribute('aHop', new InstancedBufferAttribute(hop, 3));
    geo.setAttribute('aKind', new InstancedBufferAttribute(kinds, 1));
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.name = 'audience:critters';
    root.add(mesh);
    if (tier === 'high') {
      const hsrc = critterHull();
      const hgeo = hullGeometry(hsrc);
      hsrc.dispose();
      hgeo.setAttribute('aHop', geo.getAttribute('aHop'));
      owned.push(hgeo);
      const hmat = outlineMaterial(0.022, outlineScale);
      owned.push(hmat);
      hull = new InstancedMesh(hgeo, hmat, mesh.count);
      hull.instanceMatrix = mesh.instanceMatrix;
      hull.frustumCulled = false;
      hull.name = 'audience:outline';
      // The hull bounces with the critters through the same vertex logic.
      hmat.onBeforeCompile = (shader) => {
        Object.assign(shader.uniforms, env, { uWave: waveU });
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', `#include <common>\nuniform float uTime;\nuniform float uBeat;\nuniform float uExcite;\n${PARS}${VERTEX_PARS}`)
          .replace('vec3 nrm = normal;', `vec3 nrm = normal;\n  vec3 transformed = pos;\n${VERTEX}\n  pos = transformed;`);
      };
      hmat.customProgramCacheKey = () => 'audience-outline';
      root.add(hull);
    }
  }

  return {
    root,
    addShelves(batch, bucket, outlineBucket) {
      if (count === 0) return;
      for (const sh of shelves) {
        const m = new Matrix4().makeRotationY(-sh.angle).setPosition(sh.x, sh.height, sh.y);
        const plank = roundedPlank(sh.len, SHELF_THICK, SHELF_DEPTH);
        batch.add(bucket, plank.clone(), m, { color: layout.plank, ao: [sh.height - SHELF_THICK / 2, sh.height + SHELF_THICK / 2, 0.75] });
        if (outlineBucket) batch.add(outlineBucket, plank, m);
        else plank.dispose();
        // The riser the back row stands on.
        const away = Math.atan2(sh.y - layout.cy, sh.x - layout.cx);
        const riserH = RISER_TOP - SHELF_THICK / 2 + 0.02;
        const rm = new Matrix4().makeRotationY(-sh.angle).setPosition(
          sh.x + Math.cos(away) * RISER_BACK, sh.height + SHELF_THICK / 2 + riserH / 2 - 0.01, sh.y + Math.sin(away) * RISER_BACK);
        const riser = roundedPlank(sh.len * 0.92, riserH, 0.44);
        batch.add(bucket, riser.clone(), rm, { color: layout.trim, ao: [sh.height, sh.height + RISER_TOP, 0.8] });
        if (outlineBucket) batch.add(outlineBucket, riser, rm);
        else riser.dispose();
        // A trim strip and two little balloons keeping the shelf afloat.
        const trim = new CylinderGeometry(0.06, 0.06, sh.len * 0.96, 6);
        trim.rotateZ(Math.PI / 2);
        batch.add(bucket, trim, new Matrix4().makeRotationY(-sh.angle).multiply(new Matrix4().makeTranslation(0, -0.02, SHELF_DEPTH / 2)).setPosition(
          sh.x + Math.cos(sh.angle + Math.PI / 2) * SHELF_DEPTH / 2, sh.height - 0.02, sh.y + Math.sin(sh.angle + Math.PI / 2) * SHELF_DEPTH / 2), { color: layout.trim });
        // Two balloons keep each shelf afloat: tied at the back corners, bobbing above.
        const back = Math.atan2(sh.y - layout.cy, sh.x - layout.cx);
        for (const end of [-0.4, 0.4]) {
          const bx = sh.x + Math.cos(sh.angle) * end * sh.len + Math.cos(back) * 0.75;
          const by = sh.y + Math.sin(sh.angle) * end * sh.len + Math.sin(back) * 0.75;
          const top = sh.height + 1.7 + (end > 0 ? 0.25 : 0);
          batch.add(bucket, new SphereGeometry(0.34, 10, 8), new Matrix4().makeScale(1, 1.2, 1).setPosition(bx, top, by), { color: layout.trim });
          batch.add(bucket, new ConeGeometry(0.07, 0.1, 6), new Matrix4().makeRotationX(Math.PI).setPosition(bx, top - 0.43, by), { color: layout.trim });
          const sx = sh.x + Math.cos(sh.angle) * end * sh.len + Math.cos(back) * 0.4;
          const sy = sh.y + Math.sin(sh.angle) * end * sh.len + Math.sin(back) * 0.4;
          const len = Math.hypot(bx - sx, by - sy, top - 0.48 - sh.height);
          const string = new CylinderGeometry(0.012, 0.012, len, 3);
          const dir = new Vector3(bx - sx, top - 0.48 - sh.height, by - sy).normalize();
          const q = new Matrix4().lookAt(new Vector3(), dir, new Vector3(0, 0, 1));
          string.rotateX(Math.PI / 2);
          batch.add(bucket, string, new Matrix4().makeTranslation((bx + sx) / 2, (top - 0.48 + sh.height) / 2, (by + sy) / 2).multiply(q), { color: '#5A4A6A' });
        }
      }
    },
    wave(x, y, now) {
      const v = waveU.value;
      v.x = Math.atan2(y - layout.cy, x - layout.cx);
      v.y = Math.random() < 0.5 ? 1 : -1;
      v.z = now;
    },
    dispose() {
      for (const o of owned) o.dispose();
    },
  };
}

/** A plank with rounded long edges (a squashed cylinder capsule). */
function roundedPlank(len: number, thick: number, depth: number): BufferGeometry {
  const g = new CylinderGeometry(depth / 2, depth / 2, len, 14, 1);
  g.rotateZ(Math.PI / 2);
  g.scale(1, thick / depth, 1);
  return g;
}
